import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDemandeOrigine } from '../utils/demandeOrigine';
import useTelephone from '../hooks/useTelephone';

const ContactRequestCard = ({ 
  demande, 
  onChangeStatus,
  onCall,
  onEmail,
  onAffecter,
  onDelete,
  onArchive,
  onUnarchive,
  onViewDetails,
  isArchived = false
}) => {
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const telephone = useTelephone(1279); // lot DK3 — cartes jusqu'a 1 279 px
  
  // Fonction pour obtenir les initiales
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };
  
  // Couleur du badge d'origine de la demande
  const getOrigineBadgeColor = () =>
    (demande.source === 'fiche_publique' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700');
  
  // Couleur de l'avatar avec gradient
  const getAvatarGradient = () => {
    // lot DL — plus de violet ni d'indigo : le violet est réservé à l'IA.
    const gradients = [
      'bg-gradient-to-br from-blue-500 to-blue-600',
      'bg-gradient-to-br from-pink-500 to-pink-600',
      'bg-gradient-to-br from-orange-500 to-orange-600',
      'bg-gradient-to-br from-teal-500 to-teal-600'
    ];
    const index = (demande.nom?.charCodeAt(0) || 0) % gradients.length;
    return gradients[index];
  };
  
  // Couleur du dropdown statut
  const getStatusDropdownStyle = () => {
    if (demande.statut === 'nouveau' || demande.statut === 'en_attente') 
      return 'bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100';
    if (demande.statut === 'en_cours') 
      return 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100';
    return 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100';
  };
  
  // Créer le lien du contact depuis une demande
  const handleCreateContact = () => {
    // Cette fonction doit être passée par les props
    if (window.confirm(`Créer un contact pour ${demande.nom} ?`)) {
      navigate(`/contacts/nouveau?demande_id=${demande.id}`);
    }
  };

  // lot DK — le menu ⋮ est partagé par les deux rendus (téléphone et ordinateur)
  const menuDeroulant = (classeBouton) => (
    <div className="relative">
      <button
        onClick={() => setShowMenu(!showMenu)}
        data-testid={`demande-menu-${demande.id}`}
        className={classeBouton}
        title="Plus d'actions"
      >
        <i className="bi bi-three-dots-vertical text-base"></i>
      </button>

      {showMenu && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)}></div>
          <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-20">
            {isArchived ? (
              <button
                onClick={() => { onUnarchive(demande.id); setShowMenu(false); }}
                data-testid={`demande-unarchive-${demande.id}`}
                className="w-full px-3 py-2 text-left text-sm text-blue-600 hover:bg-slate-50 flex items-center gap-2 transition"
              >
                <i className="bi bi-arrow-counterclockwise"></i>
                Désarchiver
              </button>
            ) : (
              <button
                onClick={() => { onArchive(demande.id); setShowMenu(false); }}
                data-testid={`demande-archive-${demande.id}`}
                className="w-full px-3 py-2 text-left text-sm text-amber-600 hover:bg-slate-50 flex items-center gap-2 transition"
              >
                <i className="bi bi-archive-fill"></i>
                Archiver
              </button>
            )}
            <button
              onClick={() => { onDelete(demande.id); setShowMenu(false); }}
              className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-slate-50 flex items-center gap-2 transition"
            >
              <i className="bi bi-trash-fill"></i>
              Supprimer
            </button>
          </div>
        </>
      )}
    </div>
  );

  const dateLisible = new Date(demande.created_at).toLocaleDateString('fr-FR', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });

  // lot DK — carte par demande, cibles de 44 px, rien de coupé (jusqu'a 1 279 px, lot DK3)
  if (telephone) {
    const ACTION = 'h-11 rounded-lg flex items-center justify-center gap-1.5 text-xs font-semibold transition';
    return (
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-3 space-y-2.5"
           data-testid={`demande-carte-${demande.id}`}>
        <div className="flex items-start gap-3">
          <div className={`w-11 h-11 flex-shrink-0 ${getAvatarGradient()} rounded-full flex items-center justify-center text-white font-bold text-base shadow-sm`}>
            {getInitials(demande.nom)}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-bold text-slate-800 break-words">{demande.nom}</h3>
            <div className="flex flex-wrap items-center gap-2 mt-0.5">
              <span className={`px-2 py-0.5 ${getOrigineBadgeColor()} text-[12px] font-bold rounded-full`}
                    data-testid={`demande-origine-${demande.id}`}>
                {getDemandeOrigine(demande)}
              </span>
              <span className="text-xs text-slate-500">{dateLisible}</span>
            </div>
          </div>
        </div>

        <p className="text-xs text-slate-700 truncate">
          {demande.property_reference && demande.property_id ? (
            <button
              onClick={() => navigate(`/properties/${demande.property_id}/view`)}
              data-testid={`demande-bien-ref-${demande.id}`}
              className="num font-bold text-blue-600 hover:underline"
            >
              {demande.property_reference}
            </button>
          ) : (
            <span className="font-semibold text-slate-400" data-testid={`demande-bien-ref-${demande.id}`}>
              {demande.property_reference || '—'}
            </span>
          )}
          {demande.property_titre && <span className="text-slate-500"> — {demande.property_titre}</span>}
        </p>

        {demande.message && (
          <p className="text-xs text-slate-600 line-clamp-2">{demande.message}</p>
        )}

        <div className="grid grid-cols-4 gap-2">
          <a href={`tel:${demande.telephone || ''}`}
             data-testid={`demande-appeler-${demande.id}`}
             aria-disabled={!demande.telephone}
             className={`${ACTION} ${demande.telephone
               ? 'bg-green-50 text-green-700 hover:bg-green-100'
               : 'bg-slate-50 text-slate-300 pointer-events-none'}`}
             title="Appeler">
            <i className="bi bi-telephone text-base"></i>
          </a>
          <a href={`mailto:${demande.email}`}
             data-testid={`demande-email-${demande.id}`}
             className={`${ACTION} bg-cyan-50 text-cyan-700 hover:bg-cyan-100`}
             title="Envoyer un e-mail">
            <i className="bi bi-envelope text-base"></i>
          </a>
          <button
            onClick={() => onAffecter(demande)}
            data-testid={`demande-affecter-${demande.id}`}
            className={`${ACTION} bg-blue-50 text-blue-600 hover:bg-blue-100`}
            title="Affecter à un client"
          >
            <i className="bi bi-person-check text-base"></i>
          </button>
          {menuDeroulant(`${ACTION} w-full bg-slate-50 text-slate-600 hover:bg-slate-100`)}
        </div>

        <select
          value={demande.statut === 'nouveau' ? 'en_attente' : demande.statut}
          onChange={(e) => onChangeStatus(demande.id, e.target.value)}
          data-testid={`demande-status-${demande.id}`}
          className={`w-full h-11 px-3 ${getStatusDropdownStyle()} border-2 rounded-lg text-sm font-bold cursor-pointer transition`}
        >
          <option value="en_attente">En attente</option>
          <option value="en_cours">En cours</option>
          <option value="traité">Traitée</option>
        </select>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200 px-4 py-3 hover:shadow-md transition-all duration-200">
      <div className="flex items-center gap-4">
        
        {/* Avatar */}
        <div className="flex-shrink-0">
          <div className={`w-12 h-12 ${getAvatarGradient()} rounded-full flex items-center justify-center text-white font-bold text-lg shadow-sm`}>
            {getInitials(demande.nom)}
          </div>
        </div>

        {/* Infos principales - Grid Layout */}
        <div className="flex-1 min-w-0 grid grid-cols-12 gap-3 items-center">
          
          {/* Col 1-3 : Nom + Badge + Date */}
          <div className="col-span-3">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-sm font-bold text-slate-800 truncate">{demande.nom}</h3>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={`px-2 py-0.5 ${getOrigineBadgeColor()} text-[10px] font-bold rounded-full`}
                data-testid={`demande-origine-${demande.id}`}
                title="Origine de la demande"
              >
                {getDemandeOrigine(demande)}
              </span>
              <span className="text-xs text-slate-500">
                {new Date(demande.created_at).toLocaleDateString('fr-FR', {
                  day: '2-digit',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>
          </div>

          {/* Col 4-5 : Message */}
          <div className="col-span-2">
            <p className="text-sm text-slate-600 line-clamp-2">{demande.message}</p>
          </div>

          {/* Col 6-7 : Coordonnées CLIQUABLES (texte uniquement) */}
          <div className="col-span-2">
            <a 
              href={`mailto:${demande.email}`}
              className="block text-xs text-cyan-600 hover:text-cyan-700 hover:underline mb-1 truncate"
            >
              {demande.email}
            </a>
            {demande.telephone && (
              <a 
                href={`tel:${demande.telephone}`}
                className="block text-xs text-green-600 hover:text-green-700 hover:underline truncate"
              >
                {demande.telephone}
              </a>
            )}
          </div>

          {/* Col 8-10 : Bien concerné */}
          <div className="col-span-3">
            <p className="text-xs font-semibold text-slate-800 truncate" title={demande.property_titre}>
              {demande.property_titre || '—'}
            </p>
          </div>

          {/* Col 11-12 : Réf. du bien — cliquable, ouvre la fiche du bien */}
          <div className="col-span-2 text-right">
            {demande.property_reference && demande.property_id ? (
              <button
                onClick={() => navigate(`/properties/${demande.property_id}/view`)}
                data-testid={`demande-bien-ref-${demande.id}`}
                title="Ouvrir la fiche du bien"
                className="text-xs font-bold text-blue-600 hover:text-blue-800 hover:underline whitespace-nowrap"
              >
                {demande.property_reference}
              </button>
            ) : (
              <span
                className="text-xs font-semibold text-slate-400"
                data-testid={`demande-bien-ref-${demande.id}`}
              >
                {demande.property_reference || '—'}
              </span>
            )}
          </div>

        </div>

        {/* Dropdown Statut */}
        <select
          value={demande.statut === 'nouveau' ? 'en_attente' : demande.statut}
          onChange={(e) => onChangeStatus(demande.id, e.target.value)}
          data-testid={`demande-status-${demande.id}`}
          className={`px-3 py-1.5 ${getStatusDropdownStyle()} border-2 rounded-lg text-xs font-bold cursor-pointer transition`}
        >
          <option value="en_attente">En attente</option>
          <option value="en_cours">En cours</option>
          <option value="traité">Traitée</option>
        </select>

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Affecter à un client */}
          <button
            onClick={() => onAffecter(demande)}
            className="w-9 h-9 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center transition"
            title="Affecter à un client"
            data-testid={`demande-affecter-${demande.id}`}
          >
            <i className="bi bi-person-check text-base"></i>
          </button>
          
          {/* Voir les détails */}
          <button
            onClick={() => onViewDetails(demande)}
            className="w-9 h-9 bg-slate-50 hover:bg-slate-100 text-slate-600 rounded-lg flex items-center justify-center transition"
            title="Voir les détails"
          >
            <i className="bi bi-eye text-base"></i>
          </button>
          
          {/* Menu contextuel */}
          {menuDeroulant('w-9 h-9 bg-slate-50 hover:bg-slate-100 text-slate-600 rounded-lg flex items-center justify-center transition')}
        </div>
      </div>
    </div>
  );
};

export default ContactRequestCard;
